import React from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { List, Box, Typography, Button } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import moment from "moment";
import { useTranslation } from "react-i18next";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import DashboardWidgetContainer from "../../../containers/DashboardWidget/DashboardWidgetContainer";
import AnnouncementCreateDialog from "./AnnouncementCreateDialog";
import {
  fetchAnnouncements,
  showAnnouncementModal
} from "../../../redux/actions/announcementsActions";
import { widgetKeys } from "../../../constants";
import { viewDashboardWidgets } from "../../../constants";
import { announcements as learningCenterAnnouncementsRoute } from "../../../routes/LearningCenterRoutes";
import { announcements as adminConsoleAnnouncementsRoute } from "../../../routes/AdminRoutes";

export const announcementsRoute = {
  [viewDashboardWidgets.contentTools]: adminConsoleAnnouncementsRoute,
  [viewDashboardWidgets.learningCenter]: learningCenterAnnouncementsRoute
};

const useStyles = makeStyles(theme => ({
  root: {
    "& a": {
      textDecoration: "underline"
    }
  },
  list: {
    paddingBottom: theme.spacing(2),
    width: "100%"
  },
  header: {
    paddingBottom: theme.spacing(1)
  },
  icon: {
    color: theme.palette.primary.main,
    marginLeft: theme.spacing(1)
  },
  date: {
    color: "rgba(0, 0, 0, 0.38)"
  },
  announcement: {
    paddingBottom: theme.spacing(3),
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    marginBottom: theme.spacing(3),
    "&:last-child": {
      paddingBottom: 8,
      marginBottom: 0,
      borderBottom: "none"
    }
  }
}));

export default function Announcements({ dashboard }) {
  const user = useSelector(state => state.userState.user);
  const announcements = useSelector(
    state => state.announcementsState.announcements
  );
  const loading = useSelector(state => state.announcementsState.loading);
  const dispatch = useDispatch();

  const history = useHistory();
  const { t } = useTranslation();
  const classes = useStyles();

  React.useEffect(() => {
    dispatch(fetchAnnouncements("widget"));
  }, []);

  const getAnnouncements = () => {
    let locale = user.settings.preferred_language
      ? user.settings.preferred_language
      : "en";
    return announcements.map((announcement, index) => (
      <div className={classes.announcement} key={index}>
        <Typography variant="caption" className={classes.date}>
          {t("announcements.posted")}{" "}
          {moment(announcement.postDate)
            .locale(locale)
            .format("MMM D, YYYY h:mm A")}
        </Typography>
        <Typography variant="subtitle1" className={classes.header}>
          {announcement.title}
        </Typography>
        <div
          className={classes.root}
          dangerouslySetInnerHTML={{ __html: announcement.body }}
        />
      </div>
    ));
  };

  const handleMenuItemClick = item => {
    switch (item.value) {
      case "manage":
        history.push(announcementsRoute[dashboard]);
        break;
      case "create":
        dispatch(showAnnouncementModal());
        break;
      default:
        break;
    }
  };

  const gotoList = () => {
    history.push(announcementsRoute[dashboard]);
  };

  let menuOptions = [];
  if (user.isAdmin) {
    menuOptions = [
      { value: "manage", label: t("announcements.options.manage") },
      { value: "create", label: t("announcements.options.create") }
    ];
  }

  return (
    <DashboardWidgetContainer
      dashboard={dashboard}
      widgetKey={widgetKeys.announcements}
      widgetDataLoading={loading}
      title={t("announcements.title")}
      menuProps={{
        options: menuOptions,
        onMenuItemClick: handleMenuItemClick
      }}
    >
      <Box
        data-qa="dashboard-widget-announcements"
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
      >
        <List className={classes.list}>{getAnnouncements()}</List>
        <Box alignSelf="center">
          <Button variant="text" color="primary" onClick={gotoList}>
            {t("announcements.viewAllAnnouncementsBtn")}
            <ArrowRightAltIcon className={classes.icon} />
          </Button>
        </Box>
      </Box>
      <AnnouncementCreateDialog history={history} dashboard={dashboard} />
    </DashboardWidgetContainer>
  );
}
